import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, errMsg, setToken, pickToken } from "@/lib/api";
import { useAuth, getRoles, isPlatformOwner, isSuperAdmin, isHrAdmin, isFlmAdmin, isTherapist } from "@/context/AuthContext";
import { resetAccessCache } from "@/components/auth/ProtectedRoute";
import { useT, useI18n } from "@/context/I18nContext";
import AuthLayout from "@/components/auth/AuthLayout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { labelFor } from "@/lib/roles";

/** Post-acceptance role-based routing. */
function landingFor(roles, orgType) {
  if (isPlatformOwner(roles) || isSuperAdmin(roles)) return "/admin";
  if (isHrAdmin(roles) || roles.has("business_admin")) return "/admin";
  if (isFlmAdmin(roles)) return "/admin";
  if (isTherapist(roles) || roles.has("psychologist") || roles.has("psychiatrist") || roles.has("care_coordinator") || roles.has("care_admin")) return "/app/care";
  // employee, client, or unknown → main app
  return "/app";
}

export default function AcceptInvite() {
  const t = useT();
  const { lang } = useI18n();
  const { token } = useParams();
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get(`/auth/invite/${token}`);
        if (!alive) return;
        setInfo(r.data);
        if (r.data?.email) setEmail(r.data.email);
      } catch (e) {
        setErr(errMsg(e, t("errors.notFound")));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [token, t]);

  // If already signed-in and email matches → auto-accept
  useEffect(() => {
    const auto = async () => {
      if (!user || !info?.email) return;
      const authedEmail = String(user.email || "").trim().toLowerCase();
      const inviteEmail = String(info.email || "").trim().toLowerCase();
      if (authedEmail !== inviteEmail) return;
      if (info.status && String(info.status).toLowerCase() === "accepted") return;
      try {
        setBusy(true);
        await api.post(`/auth/invite/${token}/accept`);
        resetAccessCache();
        await refresh();
        toast.success(t("auth.inviteRedeemed"));
        // We fetch me again through refresh; role-based redirect uses fresh state.
        setTimeout(() => {
          const roles = getRoles(user, null); // roles will be freshly loaded on next render; safe default
          nav(landingFor(roles, info?.organisation_type || info?.organization_type), { replace: true });
        }, 400);
      } catch (e) {
        setErr(errMsg(e, t("errors.generic")));
      } finally { setBusy(false); }
    };
    auto();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, info?.email]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    // Client-side email match check (backend must also enforce).
    if (info?.email && email.trim().toLowerCase() !== String(info.email).trim().toLowerCase()) {
      setErr(lang === "nl"
        ? "Deze uitnodiging is gestuurd naar een ander e-mailadres. Log in met het uitgenodigde e-mailadres."
        : "This invitation was sent to another email address. Please log in with the invited email.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/auth/register", {
        invite_token: token,
        email: email.trim() || null,
        name: name.trim(),
        password,
      });
      const tk = pickToken(r.data);
      if (tk) {
        setToken(tk);
        resetAccessCache();
        await refresh();
      }
      toast.success(t("auth.inviteRedeemed"));
      // Fetch me to compute correct landing.
      let landed = "/app";
      try {
        const me = await api.get("/auth/me");
        const platform = await api.get("/platform/me").catch(() => ({ data: null }));
        const roles = getRoles(me.data, platform.data);
        landed = landingFor(roles, info?.organisation_type || info?.organization_type);
      } catch { /* fall back to /app */ }
      nav(landed, { replace: true });
    } catch (e) {
      setErr(errMsg(e, t("errors.generic")));
    } finally { setBusy(false); }
  };

  if (loading) {
    return (
      <AuthLayout eyebrow={t("auth.signUp")} title={t("common.loading")}>
        <div className="text-ink-500 text-sm">{t("common.loading")}</div>
      </AuthLayout>
    );
  }

  const orgName = info?.organisation_name || info?.organization_name || info?.org_name;
  const orgType = info?.organisation_type || info?.organization_type;
  const role = info?.role;

  return (
    <AuthLayout
      eyebrow={t("auth.signUp")}
      title={t("auth.inviteTitle")}
      subtitle={t("auth.inviteSub")}
      footer={
        <>
          {t("auth.haveAccount")}{" "}
          <Link to={`/login?next=${encodeURIComponent(`/invite/${token}`)}`} data-testid="invite-goto-login" className="text-sage-600 font-semibold hover:underline">
            {t("auth.signIn")}
          </Link>
        </>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4" data-testid="invite-form">
        {(orgName || role || orgType) && (
          <div className="rounded-2xl bg-cream-100 border border-border p-4 space-y-1 text-sm" data-testid="invite-details">
            {orgName && (
              <div><span className="text-ink-500">{t("nav.organisation")}:</span> <span className="text-ink-900 font-medium">{orgName}</span></div>
            )}
            {role && (
              <div><span className="text-ink-500">{t("common.role")}:</span> <span className="text-ink-900 font-medium">{labelFor(role, lang)}</span></div>
            )}
            {orgType && (
              <div><span className="text-ink-500">Type:</span> <span className="text-ink-900 font-medium capitalize">{orgType}</span></div>
            )}
            {info?.team_name && (
              <div><span className="text-ink-500">{t("nav.teams")}:</span> <span className="text-ink-900 font-medium">{info.team_name}</span></div>
            )}
          </div>
        )}
        <div>
          <Label htmlFor="ai-name">{t("common.name")}</Label>
          <Input id="ai-name" data-testid="invite-name" required value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 h-11 rounded-xl bg-white" />
        </div>
        <div>
          <Label htmlFor="ai-email">{t("common.email")}</Label>
          <Input id="ai-email" data-testid="invite-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 h-11 rounded-xl bg-white" />
          {info?.email && email.trim().toLowerCase() !== String(info.email).trim().toLowerCase() && (
            <p className="text-xs text-clay-600 mt-1">
              {lang === "nl"
                ? `Gebruik het e-mailadres waar de uitnodiging naartoe is verstuurd (${info.email}).`
                : `Use the email address the invitation was sent to (${info.email}).`}
            </p>
          )}
        </div>
        <div>
          <Label htmlFor="ai-pw">{t("common.password")}</Label>
          <Input id="ai-pw" data-testid="invite-password" type="password" required minLength={10} value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 h-11 rounded-xl bg-white" />
          <p className="text-xs text-ink-500 mt-1">{t("auth.passwordHint")}</p>
        </div>

        {err && <div data-testid="invite-error" className="rounded-xl bg-rust-500/10 border border-rust-500/20 text-rust-600 text-sm px-3 py-2">{err}</div>}

        <Button type="submit" data-testid="invite-submit" disabled={busy} className="w-full h-11 rounded-full bg-sage-500 hover:bg-sage-600 text-white font-medium">
          {busy ? t("common.loading") : t("common.continue")}
        </Button>
      </form>
    </AuthLayout>
  );
}
